import React from 'react'
import useHide from '../hooks/useHide'
import { Box } from '@mui/material'
import { HideOn } from '../types/heroTextSection'
import { BannerProps } from '../components/Banner/Banner'
import { CustomComponentProps } from '../components/CustomComponent'
import { CallToActionProps } from '../components/CallToAction'
import { CarouselSectionProps } from '../components/LandingPage/CarouselSection'
import { PackageSectionProps } from '../components/Packages/PackageSection'
import { HeroTextSectionProps } from '../components/HeroTextSection'
import { HeroTextTwoColumnSectionProps } from '../components/HeroTextTwoColumnSection'
import { FAQGroupProps } from '../components/FAQ/FAQGroup'
import { HorizontalSplit2ColumnSectionProps } from '../components/HorizontalSplit2ColumnSection/HorizontalSplit2ColumnSection'
import { PackagesSectionProps } from '../components/Packages/PackagesSection'
import { ProductPackagesSectionProps } from '../components/ProductPackages/ProductPackagesSection'
import { ProductPackageSectionProps } from '../components/ProductPackages/ProductPackageSection'
import { ReviewSectionProps } from '../components/Reviews/ReviewSection'
import { ReviewSectionNonTrustPilotProps } from '../components/Reviews/ReviewSectionNonTrustPilot'
import { SpacerProps } from '../components/Spacer'

type ComponentProps =
  | BannerProps
  | CustomComponentProps
  | CallToActionProps
  | CarouselSectionProps
  | PackageSectionProps
  | HeroTextSectionProps
  | HeroTextTwoColumnSectionProps
  | FAQGroupProps
  | HorizontalSplit2ColumnSectionProps
  | PackagesSectionProps
  | ProductPackagesSectionProps
  | ProductPackageSectionProps
  | ReviewSectionProps
  | ReviewSectionNonTrustPilotProps
  | SpacerProps

/**
 * withHideOn is a HOC which will hide a component
 * based on the useHide hook. Use this only when the component
 * does not have a parent wrapping element like a div or section.
 * @param Component A component with the props matching the above type ComponentProps
 * @returns A wrapper around the component satisfying this functionality
 */
export function withHideOn<T extends ComponentProps>(
  Component: React.ComponentType<T>
): React.FC<T> {
  const displayName = Component.displayName || Component.name || 'Component'
  function HiddenComponent(props: T) {
    const hideClasses = useHide(getHideOnFromProps(props))
    if (!hideClasses) {
      return <Component {...props} />
    }
    return (
      <Box className={hideClasses}>
        <Component {...props} />
      </Box>
    )
  }
  HiddenComponent.displayName = displayName
  return HiddenComponent
}

function getHideOnFromProps(props: Record<string, any>): HideOn {
  for (const k in props) {
    if ('hideOn' in props) {
      return props.hideOn
    }
    if (typeof props[k] === 'object') {
      return getHideOnFromProps(props[k])
    }
  }
  return null
}
